import { ComponentProps, FC, ReactNode } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import dayjs from 'dayjs';
import {
  chartAxisTick,
  chartGridProps,
  chartTooltipContentStyle,
  chartTooltipLabelStyle,
  chartTooltipItemStyle,
} from '../../../../shared/charts/chartTheme';
import { ChartEmptyState } from '../../AthleteInjuryCharts/charts/_shared/ChartEmptyState';
import { MetricHistoryPoint } from '../useMetricHistory';

type YAxisDomain = ComponentProps<typeof YAxis>['domain'];
type YAxisTickFormatter = ComponentProps<typeof YAxis>['tickFormatter'];
type TooltipFormatter = ComponentProps<typeof Tooltip>['formatter'];

const CHART_MARGIN = { top: 4, right: 16, left: 0, bottom: 0 };

interface MetricChartFrameProps {
  data: MetricHistoryPoint[];
  height?: number;
  /** YYYY-MM-DD — giorno da evidenziare (cella cliccata). */
  highlightDate?: string | null;
  /** Colore della `ReferenceLine` disegnata sul giorno evidenziato. */
  highlightColor: string;
  /** Tipo di chart Recharts da montare: `LineChart` (RI/CMJ/HRV/HRrest/ACWR) o `BarChart` (sRPE). */
  as: 'line' | 'bar';
  yAxisDomain?: YAxisDomain;
  yAxisTickFormatter?: YAxisTickFormatter;
  tooltipFormatter: TooltipFormatter;
  /** Contenuto renderizzato prima della griglia (es. `ReferenceArea` della fascia ACWR). */
  beforeGrid?: ReactNode;
  /** Serie specifica del chart (`Line`, `Bar`, ...). */
  children: ReactNode;
}

/**
 * Scaffold Recharts condiviso dai 3 chart di `MetricHistoryModal`
 * (`MetricLineChart`/`MetricBarChart`/`MetricAcwrChart`): griglia, assi,
 * tooltip, reference line del giorno evidenziato ed empty-state. Ogni chart
 * mantiene solo la propria serie passata come `children` (e, se serve,
 * elementi extra come `ReferenceArea` via `beforeGrid`).
 */
export const MetricChartFrame: FC<MetricChartFrameProps> = ({
  data,
  height,
  highlightDate,
  highlightColor,
  as,
  yAxisDomain,
  yAxisTickFormatter,
  tooltipFormatter,
  beforeGrid,
  children,
}) => {
  if (!data || data.length === 0) {
    return <ChartEmptyState />;
  }

  const ChartComponent = as === 'bar' ? BarChart : LineChart;

  return (
    <ResponsiveContainer width="100%" height={height ?? 260}>
      <ChartComponent data={data} margin={CHART_MARGIN}>
        {beforeGrid}
        <CartesianGrid {...chartGridProps} />
        <XAxis
          dataKey="date"
          tickFormatter={(v) => dayjs(v).format('DD/MM')}
          tick={chartAxisTick}
        />
        <YAxis tick={chartAxisTick} domain={yAxisDomain} tickFormatter={yAxisTickFormatter} />
        <Tooltip
          contentStyle={chartTooltipContentStyle}
          labelStyle={chartTooltipLabelStyle}
          itemStyle={chartTooltipItemStyle}
          formatter={tooltipFormatter}
          labelFormatter={(l) => dayjs(String(l)).format('DD/MM/YYYY')}
        />
        {highlightDate && (
          <ReferenceLine
            x={highlightDate}
            stroke={highlightColor}
            strokeDasharray="4 3"
            strokeWidth={1.5}
          />
        )}
        {children}
      </ChartComponent>
    </ResponsiveContainer>
  );
};
